// src/Pages/Auth/Login.jsx
import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import { toast } from "react-toastify";
import { api, setAuthToken } from "../../services/api.js";
import { useAuth } from "../../context/AuthContext.jsx";
import { parseJwt } from "../../utils/jwt";
import { getPathForRole } from "../../utils/roleRouting";
import { Eye, EyeOff } from "lucide-react";
import logoDark from "../../assets/logo-buukkeri-dark.webp";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refresh } = useAuth();

  const [form, setForm] = useState({ email: "", password: "", remember: false });
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    try {
      const remembered = localStorage.getItem("rememberMe") === "1";
      const rememberedEmail = localStorage.getItem("rememberEmail") || "";
      if (remembered || rememberedEmail) {
        setForm((f) => ({ ...f, email: rememberedEmail, remember: remembered }));
      }
    } catch {}
  }, []);

const safeNext = useMemo(() => {
  const qs = new URLSearchParams(location.search || "");
  const raw = qs.get("next") || "";
  if (!raw) return "";

  let v = raw;

  // Jos ProtectedRoute on laittanut nextin URL-koodattuna, yritetään dekoodata
  try {
    v = decodeURIComponent(raw);
  } catch {
    v = raw;
  }

  // Sallitaan vain sivun sisäiset polut (alkaa kauttaviivalla)
  if (!v.startsWith("/")) return "";

  // Ei etusivua tai /kirjaudu paluukohteeksi
  if (v === "/" || /^\/kirjaudu(?:$|[/?#])/i.test(v)) return "";

  return v;
}, [location.search]);


  useEffect(() => {
    if (location.state?.resetOk) {
      toast.success("Salasana vaihdettu! Kirjaudu sisään uudella salasanalla.", { containerId: "global" });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (user && !safeNext) {
      const dest = getPathForRole(user.role || "performer");
      try { if (dest.startsWith("/dashboard")) sessionStorage.setItem("showWelcomeToast", "1"); } catch {}
      navigate(dest, { replace: true });
    }
  }, [user, safeNext, navigate]);

  const verifiedOk = useMemo(() => {
    const qs = new URLSearchParams(location.search || "");
    return qs.get("verified") === "1" || Boolean(location.state?.verifiedOk);
  }, [location.search, location.state]);

  useEffect(() => {
    if (verifiedOk) {
      toast.success("Sähköposti vahvistettu! Nyt voit kirjautua sisään ja aloittaa tilin käyttöönoton.", { containerId: "global" });
      const qs = new URLSearchParams(location.search || "");
      qs.delete("verified");
      const search = qs.toString();
      navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true, state: {} });
    }
  }, [verifiedOk, navigate, location.pathname, location.search]);

  const validate = (f) => {
    const e = {};
    if (!f.email.trim()) e.email = "Sähköposti vaaditaan.";
    else if (!emailRegex.test(f.email.trim())) e.email = "Sähköposti ei ole kelvollinen.";
    if (!f.password) e.password = "Salasana vaaditaan.";
    return e;
  };
  const runValidation = (next) => { const v = validate(next); setErrors(v); return v; };
  const isValid = Object.keys(validate(form)).length === 0;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const next = { ...form, [name]: type === "checkbox" ? checked : value };
    setForm(next);
    if (touched[name]) runValidation(next);
  };
  const handleBlur = (e) => { setTouched({ ...touched, [e.target.name]: true }); runValidation(form); };

  const liveErrors = useMemo(() => {
    const parts = [];
    if (touched.email && errors.email) parts.push(`Sähköposti: ${errors.email}`);
    if (touched.password && errors.password) parts.push(`Salasana: ${errors.password}`);
    return parts.join(" · ");
  }, [errors, touched]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const v = runValidation(form);
    if (Object.keys(v).length) return;

    setLoading(true);
    try {
      const { token } = await api.auth.login(form.email.trim(), form.password, { remember: form.remember });
      setAuthToken(token);

      try {
        if (form.remember) {
          localStorage.setItem("rememberMe", "1");
          localStorage.setItem("rememberEmail", form.email.trim());
        } else {
          localStorage.removeItem("rememberMe");
          localStorage.removeItem("rememberEmail");
        }
      } catch {}

      try { await refresh?.(); } catch {}

      let u;
      try {
        const me = await api.auth.me();
        u = me?.user || me || {};
      } catch { u = {}; }

      if (!u.accepted_terms_at || !u.accepted_privacy_at) {
        navigate("/onboarding", { replace: true });
        return;
      }

      let bandsCount = 0;
      try {
        const res = await api.auth.meBands();
        const list =
          Array.isArray(res?.bands) ? res.bands :
          Array.isArray(res?.memberships) ? res.memberships :
          Array.isArray(res?.items) ? res.items :
          Array.isArray(res) ? res : [];
        bandsCount = list.length;
      } catch {}

      if (bandsCount === 0) {
        navigate("/onboarding", { replace: true });
        return;
      }

      const markWelcomeIfDashboardy = (target) => {
        try {
          if (!target || /^\/(?:dashboard|start)(?:$|[/?#])/.test(target)) {
            sessionStorage.setItem("showWelcomeToast", "1");
          }
        } catch {}
      };

      const role = u?.role || parseJwt(token)?.role || "performer";
      const defaultDash = getPathForRole(role) || "/dashboard";
      const fromPath = location.state?.from?.pathname || "";
      // Jos tullaan etusivulta tai /kirjaudu-sivulta, älä käytä sitä paluukohteena
      const useFrom = fromPath && fromPath !== "/" && !/^\/kirjaudu(?:$|[/?#])/.test(fromPath);
      const fallback = useFrom ? fromPath : defaultDash;
      const dest = safeNext || fallback;

      const destPath = typeof dest === "string" ? dest : (dest?.pathname || "");
      const isDashboard = /^\/dashboard(?:[/?#]|$)/.test(destPath);
      if (isDashboard) markWelcomeIfDashboardy(destPath);

      navigate(dest, { replace: true, state: isDashboard ? { welcomeFromLogin: true } : undefined });
    } catch (err) {
      const code = err?.payload?.error;
      if (err?.status === 429) {
        toast.error("Liian monta yritystä. Yritä hetken päästä uudelleen.", { containerId: "global" });
      } else if (err?.status === 401 || code === "invalid_credentials") {
        toast.error("Virheellinen sähköposti tai salasana.", { containerId: "global" });
      } else if (code === "account_disabled") {
        toast.error("Tili on poistettu käytöstä. Ota yhteys ylläpitoon.", { containerId: "global" });
      } else {
        toast.error("Kirjautuminen epäonnistui. Yritä uudelleen.", { containerId: "global" });
      }
    } finally {
      setLoading(false);
    }
  };

  const labelBase =
    "block font-customFont1 text-sm uppercase tracking-wide text-black/70 dark:text-white/80 mb-1";
  const inputBase =
    "w-full h-11 rounded-lg px-3 font-customFont1 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-pink/60 dark:focus:ring-primary text-base";
  const helpBase = "mt-1 text-xs";

  return (
    <div className="min-h-screen bg-lemon/60 dark:bg-black/40">
      <div
        className={[
          "mx-auto max-w-6xl px-4 pb-12",
          "pt-24",
          "md:min-h-[calc(100vh-96px)] md:grid md:place-items-center",
        ].join(" ")}
      >
        <div className="w-full md:max-w-4xl lg:max-w-5xl mx-auto">
          {/* Logo */}
          <div className="mb-4 md:mb-6 flex justify-center">
            <Link to="/" aria-label="Siirry etusivulle" className="inline-flex items-center">
              <img
                src={logoDark}
                alt="Buukkeri"
                className="h-24 w-auto md:h-28"
                loading="lazy"
                decoding="async"
              />
            </Link>
          </div>

          {/* Otsikko keskelle myös mobiilissa */}
          <h1 className="text-2xl font-bold text-center text-black/80 dark:text-white/90 mb-3">
            Kirjaudu sisään
          </h1>

          <div
            className={[
              "grid grid-cols-1 gap-6",
              "md:grid-cols-2 md:items-start md:justify-center",
            ].join(" ")}
          >
            {/* Lomake */}
            <div className="order-2 md:order-2">
              <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/60 backdrop-blur-md shadow-lg w-full p-4 sm:p-5">
                <div aria-live="polite" role="status" className="sr-only">
                  {liveErrors}
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                  <div>
                    <label className={labelBase} htmlFor="email">Sähköposti</label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={inputBase}
                      autoComplete="email"
                      required
                      aria-invalid={!!(touched.email && errors.email)}
                      aria-describedby={touched.email && errors.email ? "email-error" : undefined}
                      disabled={loading}
                    />
                    {touched.email && errors.email && (
                      <p id="email-error" className={`${helpBase} text-red-600 dark:text-red-400`} role="alert">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className={labelBase} htmlFor="password">Salasana</label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPwd ? "text" : "password"}
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`${inputBase} pr-11`}
                        autoComplete="current-password"
                        required
                        aria-invalid={!!(touched.password && errors.password)}
                        aria-describedby={touched.password && errors.password ? "password-error" : undefined}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-md text-black/60 dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-pink/60 dark:focus:ring-primary"
                        aria-label={showPwd ? "Piilota salasana" : "Näytä salasana"}
                        aria-pressed={showPwd}
                        title={showPwd ? "Piilota salasana" : "Näytä salasana"}
                        disabled={loading}
                      >
                        {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    {touched.password && errors.password && (
                      <p id="password-error" className={`${helpBase} text-red-600 dark:text-red-400`} role="alert">
                        {errors.password}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <label htmlFor="remember" className="inline-flex items-center gap-2 text-black/70 dark:text-white/80 text-sm">
                      <input
                        id="remember"
                        type="checkbox"
                        name="remember"
                        checked={form.remember}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-black/20 dark:border-white/20"
                        disabled={loading}
                      />
                      <span>Muista minut</span>
                    </label>

                    <Link to="/forgot" className="text-pink underline hover:opacity-80 dark:text-primary text-sm">
                      Unohtuiko salasana?
                    </Link>
                  </div>

                  <NeonButton
                    type="submit"
                    variant="primary"
                    size="md"
                    className="w-full flex items-center justify-center disabled:opacity-60"
                    disabled={loading || !isValid}
                    aria-disabled={loading || !isValid}
                  >
                    <span className="inline-flex items-center gap-2">
                      {loading && (
                        <span className="relative inline-flex h-5 w-5 shrink-0">
                          <svg
                            className="animate-spin h-5 w-5 static"
                            style={{ position: "static" }}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            role="status"
                            aria-label="Ladataan"
                            focusable="false"
                            aria-hidden="true"
                          >
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
                            <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                          </svg>
                        </span>
                      )}
                      <span>{loading ? "Kirjaudutaan…" : "Kirjaudu"}</span>
                    </span>
                  </NeonButton>
                </form>

                <p className="mt-6 text-center text-sm text-black/60 dark:text-white/60">
                  Eikö sinulla ole tiliä?{" "}
                  <Link to="/rekisteroidy" className="text-pink underline hover:opacity-80 dark:text-primary">
                    Luo esiintyjätili
                  </Link>
                  .
                </p>
              </div>
            </div>

            {/* Info/CTA */}
            <div className="order-1 md:order-1">
              <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 backdrop-blur p-4">
                <p className="text-black/80 dark:text-white/80 text-sm leading-relaxed text-center">
                  Kirjaudu palveluun hallinnoidaksesi esiintyjäprofiiliasi.{" "}
                  Uusi käyttäjä?{" "}
                  <Link
                    to="/rekisteroidy"
                    className="font-semibold underline text-pink dark:text-primary hover:opacity-90"
                  >
                    Luo esiintyjätili
                  </Link>
                  .
                </p>

                <div className="mt-4 flex justify-center">
                  <NeonButton as={Link} to="/" variant="secondary" size="sm">
                    Palaa etusivulle
                  </NeonButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
}
